import { useEffect, useState } from "react";
import { Pagination, Table } from "react-bootstrap";
import { DateTime } from "luxon";
import styles from "./selfServiceSettings.module.scss";
import { ErrorToast } from "./selfServiceSettings";

const PAGE_SIZE = 20;

interface ScribeHistoryRecord {
    username: string;
    start: string;
    downloadLink: string | null;
}

interface ScribeHistoryResponse {
    history: ScribeHistoryRecord[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export function SelfServiceUploadHistory({ ujID }: { ujID: number }) {
    const [page, setPage] = useState(0);
    const [historyRows, setHistoryRows] = useState<ScribeHistoryRecord[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        setPage(0);
    }, [ujID]);

    useEffect(() => {
        setIsLoading(true);
        setErrorMessage("");

        fetch(
            `/api/v1/uj/${ujID}/scribe/history?page=${page}&pageSize=${PAGE_SIZE}`,
        )
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        "Failed to load upload history. Please try again.",
                    );
                }
                return response.json();
            })
            .then((data: ScribeHistoryResponse) => {
                setHistoryRows(data.history);
                setTotalCount(data.totalCount);
            })
            .catch((error: Error) => {
                setErrorMessage(error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [ujID, page]);

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    function buildPaginationItems() {
        const items = [];
        const maxVisible = 5;
        const halfVisible = Math.floor(maxVisible / 2);

        let startPage = Math.max(0, page - halfVisible);
        let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(0, endPage - maxVisible + 1);
        }

        if (startPage > 0) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
            items.push(
                <Pagination.Item
                    key={pageIndex}
                    active={pageIndex === page}
                    onClick={() => setPage(pageIndex)}
                >
                    {pageIndex + 1}
                </Pagination.Item>,
            );
        }

        if (endPage < totalPages - 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        return items;
    }

    return (
        <div>
            {errorMessage && (
                <ErrorToast
                    errorMessage={errorMessage}
                    clearToast={() => setErrorMessage("")}
                />
            )}

            {isLoading ? (
                <p>Loading...</p>
            ) : (
                <>
                    {historyRows.length === 0 ? (
                        <p>No upload history found.</p>
                    ) : (
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Username</th>
                                    <th>Uploaded At</th>
                                    <th>Download</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyRows.map((record, index) => (
                                    <tr key={index}>
                                        <td>{record.username}</td>
                                        <td>
                                            {DateTime.fromISO(
                                                record.start,
                                            ).toLocaleString(
                                                DateTime.DATETIME_MED,
                                            )}
                                        </td>
                                        <td>
                                            {record.downloadLink !== null ? (
                                                <a href={record.downloadLink}>
                                                    Download
                                                </a>
                                            ) : (
                                                <span
                                                    title={
                                                        "Version is too old to download"
                                                    }
                                                >
                                                    No longer available
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    {totalPages > 1 && (
                        <div
                            className="d-flex justify-content-center"
                            data-testid={"pagination-container"}
                        >
                            <Pagination className={styles.formGroup}>
                                <Pagination.First
                                    disabled={page === 0}
                                    onClick={() => setPage(0)}
                                />
                                <Pagination.Prev
                                    disabled={page === 0}
                                    onClick={() => setPage(page - 1)}
                                />
                                {buildPaginationItems()}
                                <Pagination.Next
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage(page + 1)}
                                />
                                <Pagination.Last
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage(totalPages - 1)}
                                />
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
